import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtNum } from "../lib/format";
import { Search } from "lucide-react";

export default function Finance() {
    const [tb, setTb] = useState(null);
    const [monthly, setMonthly] = useState(null);
    const [filter, setFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("All");

    useEffect(() => {
        api.get("/finance/trial-balance").then((r) => setTb(r.data));
        api.get("/finance/monthly").then((r) => setMonthly(r.data));
    }, []);

    if (!tb) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING TRIAL BALANCE...</div>;

    const filtered = tb.entries.filter((e) => (typeFilter === "All" || e.type === typeFilter) && (!filter || e.name.toLowerCase().includes(filter.toLowerCase()) || e.code.includes(filter)));

    const byType = {};
    tb.entries.forEach((e) => {
        byType[e.type] = byType[e.type] || { debit: 0, credit: 0 };
        byType[e.type].debit += e.debit;
        byType[e.type].credit += e.credit;
    });

    const revenue = (byType.Income?.credit || 0) - (byType.Income?.debit || 0);
    const expense = (byType.Expense?.debit || 0) - (byType.Expense?.credit || 0);
    const profit = revenue - expense;

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// FINANCE MODULE</div>
                <h1 className="font-display font-black text-3xl">Trial Balance & P&L</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">As of 15-Feb-2026 · FY 25-26</div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="fin-kpi-revenue" label="Total Revenue" value={revenue} sub="YTD" delta={9.4} />
                <KpiCard testId="fin-kpi-expense" label="Total Expenses" value={expense} sub="YTD" delta={7.1} />
                <KpiCard testId="fin-kpi-profit" label="Net Profit" value={profit} sub={`Margin ${(profit/revenue*100).toFixed(1)}%`} delta={14.2} accent />
                <KpiCard testId="fin-kpi-tb-check" label="TB Debit / Credit" value={tb.total_debit} format="custom" sub={`Cr ${fmtINR(tb.total_credit)}`} />
            </div>

            <Panel testId="tb-panel" title="Trial Balance" subtitle={`${tb.entries.length} accounts`} right={
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                        <input
                            data-testid="tb-search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Search..."
                            className="bg-[var(--bg-base)] border border-panel pl-7 pr-2 py-1 text-xs font-mono-fin w-40"
                        />
                    </div>
                    <select
                        data-testid="tb-type-filter"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs font-mono-fin uppercase tracking-wider"
                    >
                        {["All", "Asset", "Liability", "Equity", "Income", "Expense"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                </div>
            }>
                <div className="overflow-x-auto">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel">
                            <tr className="text-[10px] uppercase tracking-[0.1em] text-[var(--text-secondary)]">
                                <th className="text-left py-2">Code</th>
                                <th className="text-left">Account</th>
                                <th className="text-left">Type</th>
                                <th className="text-right">Debit</th>
                                <th className="text-right">Credit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((e) => (
                                <tr key={e.code} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                    <td className="py-2 text-[var(--text-secondary)]">{e.code}</td>
                                    <td className="text-[var(--text-primary)]">{e.name}</td>
                                    <td className="text-[var(--text-secondary)] uppercase text-[10px] tracking-wider">{e.type}</td>
                                    <td className="text-right text-[var(--text-primary)]">{e.debit ? fmtINR(e.debit) : "—"}</td>
                                    <td className="text-right text-[var(--text-primary)]">{e.credit ? fmtINR(e.credit) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="border-t border-[var(--brand-amber)]">
                            <tr className="text-xs uppercase tracking-wider">
                                <td colSpan={3} className="py-2 text-[var(--brand-amber)]">TOTAL</td>
                                <td className="text-right text-[var(--text-primary)]">{fmtINR(tb.total_debit)}</td>
                                <td className="text-right text-[var(--text-primary)]">{fmtINR(tb.total_credit)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </Panel>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {["Asset", "Liability", "Equity", "Income", "Expense"].map((t) => (
                    <Panel key={t} title={t} className="!p-0">
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Debit</div>
                        <div className="font-mono-fin text-lg text-[var(--text-primary)]">{fmtINR(byType[t]?.debit || 0)}</div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-3 mb-1">Credit</div>
                        <div className="font-mono-fin text-lg text-[var(--text-primary)]">{fmtINR(byType[t]?.credit || 0)}</div>
                    </Panel>
                ))}
            </div>
        </div>
    );
}
